// Per-report table "view": column order / widths / pins / sort / filters / density / grouping. Saved
// to the backend (/api/v1/preferences/{report.columns}) so it follows the user across devices, with
// a localStorage mirror for instant load and offline. Named "saved views" live alongside it.

import { api } from "./api";
import { REPORTS, type ReportKey } from "./reportConfig";
import { getPrefs } from "./prefs";

export interface SortRule {
  key: string;
  dir: "asc" | "desc";
}

/** Per-column filter. Only the fields relevant to the column type are used. */
export interface ColFilter {
  contains?: string; // text / name — case-insensitive substring
  values?: string[]; // exact-value whitelist (category / brand / status)
  min?: number; // num / money
  max?: number;
  from?: string; // date (ISO)
  to?: string;
  blank?: "only" | "hide"; // show only empty cells / hide empty cells
  zeros?: "only" | "hide"; // show only zero values / hide zero values (numeric columns)
}

export type AggFn = "sum" | "avg" | "min" | "max" | "median" | "count" | "pct" | "none";

/** A user-defined column: `expr` is an arithmetic formula over other column keys. */
export interface CalcColumn {
  key: string; // always prefixed "calc:"
  label: string;
  expr: string;
  kind: "num" | "money" | "pct";
}

export interface ViewState {
  order: string[];
  widths: Record<string, number>;
  pinned: string[];
  pinnedRight: string[];
  sort: SortRule[]; // multi-column, in priority order
  filters: Record<string, ColFilter>;
  agg: Record<string, AggFn>; // per-column aggregate for the totals / group rows
  calc: CalcColumn[]; // user-defined calculated columns
  density: "compact" | "default" | "comfortable";
  groupBy: string[];
  expanded: Record<string, boolean>;
  collapsedByDefault: boolean; // grouped: start collapsed
  pivotByDate?: boolean; // date-grained reports only: dates as column groups instead of rows
  pivotMetrics?: string[]; // which metric columns show under each date in pivot mode; unset = all
}

export interface SavedView {
  id: string;
  name: string;
  view: ViewState;
  period?: string; // report date-range preset
  q?: string; // free-text row filter
  pin?: boolean; // show as a shortcut in the sidebar
  fav?: boolean; // open this view by default for the report
}

export function defaultView(key: ReportKey): ViewState {
  const r = REPORTS[key];
  return {
    order: [...r.defaultVisible],
    widths: {},
    pinned: [...r.defaultPinned],
    pinnedRight: [],
    sort: [...(r.defaultSort ?? [])],
    filters: {},
    agg: {},
    calc: [],
    density: getPrefs().density,
    groupBy: [...(r.defaultGroupBy ?? [])],
    expanded: {},
    collapsedByDefault: !!r.defaultGroupBy?.length,
  };
}

function lsKey(key: ReportKey) {
  return `wa_view_${key}`;
}
function prefKey(key: ReportKey) {
  return `report.${key}.view`;
}
function savedLsKey(key: ReportKey) {
  return `wa_saved_${key}`;
}
function savedPrefKey(key: ReportKey) {
  return `report.${key}.saved`;
}

function sanitizeFilters(raw: unknown, valid: Set<string>): Record<string, ColFilter> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, ColFilter> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!valid.has(k) || !v || typeof v !== "object") continue;
    const f = v as ColFilter;
    const clean: ColFilter = {};
    if (typeof f.contains === "string" && f.contains) clean.contains = f.contains;
    if (Array.isArray(f.values) && f.values.length) clean.values = f.values.map(String);
    if (typeof f.min === "number") clean.min = f.min;
    if (typeof f.max === "number") clean.max = f.max;
    if (typeof f.from === "string" && f.from) clean.from = f.from;
    if (typeof f.to === "string" && f.to) clean.to = f.to;
    if (f.blank === "only" || f.blank === "hide") clean.blank = f.blank;
    if (f.zeros === "only" || f.zeros === "hide") clean.zeros = f.zeros;
    if (Object.keys(clean).length) out[k] = clean;
  }
  return out;
}

export function filterActive(f: ColFilter | undefined): boolean {
  return !!f && Object.keys(f).length > 0;
}

function sanitize(key: ReportKey, raw: unknown): ViewState {
  const base = defaultView(key);
  if (!raw || typeof raw !== "object") return base;
  const v = raw as Partial<ViewState>;
  const valid = new Set(REPORTS[key].columns.map((c) => c.key));
  const calc: CalcColumn[] = Array.isArray(v.calc)
    ? v.calc
        .filter((c) => c && typeof c.expr === "string" && typeof c.label === "string")
        .map((c) => ({
          key: String(c.key || `calc:${Math.random().toString(36).slice(2)}`),
          label: String(c.label).slice(0, 40),
          expr: String(c.expr).slice(0, 200),
          kind: c.kind === "money" || c.kind === "pct" ? c.kind : "num",
        }))
    : [];
  const calcKeys = new Set(calc.map((c) => c.key));
  const knownKey = (k: string) => valid.has(k) || calcKeys.has(k);
  let sort: SortRule[] = [];
  if (Array.isArray(v.sort)) sort = v.sort.filter((s) => s && knownKey(s.key));
  else if (v.sort && typeof v.sort === "object" && "key" in v.sort) sort = [v.sort as SortRule];
  const aggIn = v.agg && typeof v.agg === "object" ? (v.agg as Record<string, AggFn>) : {};
  const agg: Record<string, AggFn> = {};
  for (const [k, fn] of Object.entries(aggIn)) {
    if (knownKey(k) && ["sum", "avg", "min", "max", "median", "count", "pct", "none"].includes(fn)) agg[k] = fn;
  }
  return {
    order: Array.isArray(v.order) ? v.order.filter(knownKey) : base.order,
    widths: v.widths && typeof v.widths === "object" ? v.widths : {},
    pinned: Array.isArray(v.pinned) ? v.pinned.filter(knownKey) : base.pinned,
    pinnedRight: Array.isArray(v.pinnedRight) ? v.pinnedRight.filter(knownKey) : [],
    sort,
    filters: sanitizeFilters(v.filters, valid),
    agg,
    calc,
    density: v.density ?? base.density,
    groupBy: Array.isArray(v.groupBy) ? v.groupBy : [],
    expanded: v.expanded && typeof v.expanded === "object" ? v.expanded : {},
    collapsedByDefault: Boolean(v.collapsedByDefault),
  };
}

export function loadViewLocal(key: ReportKey): ViewState {
  try {
    return sanitize(key, JSON.parse(localStorage.getItem(lsKey(key)) ?? "null"));
  } catch {
    return defaultView(key);
  }
}

/** Load from the server (falls back to local / default on any failure). */
export async function loadView(key: ReportKey): Promise<ViewState> {
  try {
    const res = await api.get<Record<string, unknown>>("/preferences");
    const server = res.data[prefKey(key)];
    if (server) {
      const v = sanitize(key, server);
      try {
        localStorage.setItem(lsKey(key), JSON.stringify(v));
      } catch {
        /* ignore */
      }
      return v;
    }
  } catch {
    /* offline / auth off — use local */
  }
  return loadViewLocal(key);
}

const saveTimer: Record<string, ReturnType<typeof setTimeout>> = {};
export function saveView(key: ReportKey, v: ViewState): void {
  try {
    localStorage.setItem(lsKey(key), JSON.stringify(v));
  } catch {
    /* ignore */
  }
  clearTimeout(saveTimer[key]);
  saveTimer[key] = setTimeout(() => {
    api.put(`/preferences/${prefKey(key)}`, { value: v }).catch(() => undefined);
  }, 600);
}

export function resetView(key: ReportKey): ViewState {
  try {
    localStorage.removeItem(lsKey(key));
  } catch {
    /* ignore */
  }
  const v = defaultView(key);
  api.put(`/preferences/${prefKey(key)}`, { value: v }).catch(() => undefined);
  return v;
}

/* ---------------- saved views ---------------- */

function sanitizeSaved(key: ReportKey, raw: unknown): SavedView[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((s) => s && typeof s === "object" && typeof s.name === "string")
    .map((s) => ({
      id: String(s.id ?? Math.random().toString(36).slice(2)),
      name: String(s.name).slice(0, 60),
      view: sanitize(key, s.view),
      period: typeof s.period === "string" ? s.period : undefined,
      q: typeof s.q === "string" ? s.q : undefined,
      pin: Boolean(s.pin),
      fav: Boolean(s.fav),
    }));
}

export const REPORT_KEYS: ReportKey[] = ["current", "daily", "weekly", "monthly", "suppliers", "brands"];

/** One /preferences call → saved views for every report (for the sidebar). Falls back to local. */
export async function loadAllSaved(): Promise<Record<ReportKey, SavedView[]>> {
  const out = { current: [], daily: [], weekly: [], monthly: [], suppliers: [], brands: [] } as Record<ReportKey, SavedView[]>;
  try {
    const res = await api.get<Record<string, unknown>>("/preferences");
    for (const k of REPORT_KEYS) {
      const server = res.data[savedPrefKey(k)];
      out[k] = server !== undefined ? sanitizeSaved(k, server) : loadSavedLocal(k);
    }
  } catch {
    for (const k of REPORT_KEYS) out[k] = loadSavedLocal(k);
  }
  return out;
}

/** Encode a view + period + q into a short URL-safe string for the "share link" feature. */
export function encodeShare(view: ViewState, period?: string, q?: string): string {
  try {
    const json = JSON.stringify({ view, period, q });
    return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return "";
  }
}

export function decodeShare(
  key: ReportKey,
  s: string,
): { view: ViewState; period?: string; q?: string } | null {
  try {
    const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(escape(atob(b64)));
    const raw = JSON.parse(json) as { view?: unknown; period?: string; q?: string };
    return { view: sanitize(key, raw.view), period: raw.period, q: raw.q };
  } catch {
    return null;
  }
}

export function loadSavedLocal(key: ReportKey): SavedView[] {
  try {
    return sanitizeSaved(key, JSON.parse(localStorage.getItem(savedLsKey(key)) ?? "null"));
  } catch {
    return [];
  }
}

export async function loadSaved(key: ReportKey): Promise<SavedView[]> {
  try {
    const res = await api.get<Record<string, unknown>>("/preferences");
    const server = res.data[savedPrefKey(key)];
    if (server !== undefined) {
      const list = sanitizeSaved(key, server);
      try {
        localStorage.setItem(savedLsKey(key), JSON.stringify(list));
      } catch {
        /* ignore */
      }
      return list;
    }
  } catch {
    /* offline */
  }
  return loadSavedLocal(key);
}

export function persistSaved(key: ReportKey, list: SavedView[]): void {
  try {
    localStorage.setItem(savedLsKey(key), JSON.stringify(list));
  } catch {
    /* ignore */
  }
  api.put(`/preferences/${savedPrefKey(key)}`, { value: list }).catch(() => undefined);
}
